/**
 * Quels rappels sont dus aujourd'hui — logique pure
 *
 * Le planificateur tourne toutes les heures ; ce module décide *quoi* envoyer,
 * sans rien savoir des canaux ni de la base. C'est ce qui le rend testable au
 * jour près, sur des cas qu'on ne pourrait pas provoquer en production.
 *
 * Deux choix de fond, tous les deux dictés par l'usage :
 *
 *  - **La relance de retard est hebdomadaire, jamais quotidienne.** Une
 *    application qui répète tous les jours qu'on est en retard se fait
 *    désinstaller. Une fois par semaine rappelle sans harceler.
 *
 *  - **Les heures de silence décalent, elles ne suppriment pas.** Un rappel
 *    prévu à 22 h n'est pas perdu : il part à la fin de la période de silence.
 *    Le supprimer serait un bug silencieux, exactement ce qu'une application de
 *    rappels ne peut pas se permettre.
 */
import { ajouteJours, joursEntre } from './dates.js';
export const RELANCES = ['jamais', 'quotidienne', 'hebdomadaire', 'mensuelle'];
export const LIBELLE_RELANCE = {
    jamais: 'Jamais',
    quotidienne: 'Chaque jour',
    hebdomadaire: 'Chaque semaine',
    mensuelle: 'Chaque mois',
};
/** Période en jours. `null` quand il ne faut pas relancer du tout. */
const PERIODE = {
    jamais: null,
    quotidienne: 1,
    hebdomadaire: 7,
    mensuelle: 30,
};
/**
 * Faut-il relancer aujourd'hui ?
 *
 * Le rythme se compte en jours de retard, pas en jours de calendrier : une tâche
 * en retard de 14 jours est relancée le 7e et le 14e en hebdomadaire, quel que
 * soit le jour de la semaine. C'est ce qui rend l'offset — négatif, égal au
 * retard — unique d'une relance à l'autre, et donc l'anti-doublon efficace sans
 * table supplémentaire.
 *
 * « Mensuelle » vaut 30 jours et non « le même quantième » : sur un retard, la
 * régularité importe plus que la date exacte, et 30 évite les mois de février.
 *
 * Le seuil d'escalade reste honoré **même en « jamais »** : prévenir l'autre
 * parent d'un retard important n'est pas une relance de confort, c'est le seul
 * mécanisme qui répartit la charge au lieu de la déplacer.
 */
export function estJourDeRelance(retard, escaladeJours, relance = 'hebdomadaire') {
    if (retard <= 0)
        return false;
    if (escaladeJours !== null && retard === escaladeJours)
        return true;
    const periode = PERIODE[relance];
    return periode !== null && retard % periode === 0;
}
/** Rappels dus le jour donné, pour un lot de tâches. */
export function rappelsDus(taches, aujourdhui, relance = 'hebdomadaire') {
    const dus = [];
    for (const tache of taches) {
        if (tache.statut !== 'active' || !tache.echeance)
            continue;
        const reference = tache.resiliationLimite ?? tache.echeance;
        const retard = Math.max(0, joursEntre(tache.echeance, aujourdhui));
        // Le seuil d'escalade n'existe que s'il y a quelqu'un à prévenir : sans
        // destinataire, il n'a aucune raison d'altérer le rythme des relances.
        const seuil = tache.escaladeVers !== null ? tache.escaladeJours : null;
        const escalade = seuil !== null && retard >= seuil && retard > 0;
        // Rappels avant l'échéance (ou avant la fermeture de la fenêtre de résiliation).
        for (const offset of new Set(tache.rappels)) {
            if (!Number.isInteger(offset) || offset < 0)
                continue;
            if (ajouteJours(reference, -offset) === aujourdhui) {
                dus.push({ tache, echeance: tache.echeance, offsetJours: offset, retard, escalade });
            }
        }
        // Relance de retard, au rythme choisi par l'utilisateur — sauf sourdine
        // posée sur cette tâche, traduite en « jamais » plutôt qu'en branche à
        // part. La règle de « jamais » est déjà exactement celle qu'on veut : plus
        // rien de périodique, mais le seuil d'escalade toujours honoré. Mettre une
        // tâche en sourdine me libère, moi ; ça ne prive pas l'autre parent de
        // l'alerte sur un retard important — et une seconde branche aurait perdu
        // cette garantie sans que personne ne le voie, puisqu'elle ne se manifeste
        // que sur un gros retard.
        if (estJourDeRelance(retard, seuil, tache.relanceMuette ? 'jamais' : relance)) {
            dus.push({ tache, echeance: tache.echeance, offsetJours: -retard, retard, escalade });
        }
    }
    return dus;
}
/**
 * Rappels dus sur une fenêtre de jours, dédupliqués.
 *
 * Deux usages, et le second n'est pas un luxe :
 *
 *  - une catégorie épinglée à un jour de la semaine (« les travaux le samedi
 *    matin ») doit rattraper les rappels de la semaine écoulée ;
 *  - un serveur familial qui a redémarré ne doit pas avaler les rappels de la
 *    veille. Une petite fenêtre de rattrapage les récupère, et l'anti-doublon
 *    garantit qu'aucun ne partira deux fois.
 */
export function rappelsDusEntre(taches, du, au, relance = 'hebdomadaire') {
    const vus = new Map();
    for (let jour = du; jour <= au; jour = ajouteJours(jour, 1)) {
        for (const r of rappelsDus(taches, jour, relance)) {
            vus.set(`${r.tache.id}|${r.echeance}|${r.offsetJours}`, r);
        }
    }
    return [...vus.values()];
}
export function heureDe(heure) {
    if (!heure)
        return null;
    const h = Number(heure.slice(0, 2));
    return Number.isInteger(h) && h >= 0 && h <= 23 ? h : null;
}
/** La fenêtre peut enjamber minuit : 21 h → 7 h couvre 21, 22, 23, 0 … 6. */
export function dansLeSilence(heure, silence) {
    if (!silence.actif)
        return false;
    const de = heureDe(silence.de);
    const a = heureDe(silence.a);
    if (de === null || a === null || de === a)
        return false;
    return de < a ? heure >= de && heure < a : heure >= de || heure < a;
}
/**
 * Heure à laquelle un rappel partira réellement. Un rappel tombant dans la
 * période de silence est repoussé à sa fin, pas annulé.
 */
export function heureEffective(heureVoulue, silence) {
    if (!dansLeSilence(heureVoulue, silence))
        return heureVoulue;
    return heureDe(silence.a) ?? heureVoulue;
}
/**
 * Niveau du lot, pour choisir le ton du message et la priorité ntfy. On prend
 * le plus urgent : un lot contenant une tâche en retard est un lot en retard.
 */
export function niveauLot(rappels) {
    if (rappels.some((r) => r.retard > 0))
        return 'retard';
    if (rappels.some((r) => r.offsetJours === 0))
        return 'aujourdhui';
    return 'bientot';
}
/**
 * Phrase de synthèse, dans le ton du produit : on annonce ce qu'il y a à faire,
 * sans culpabiliser sur ce qui n'a pas été fait.
 */
export function resumeLot(rappels) {
    const nombre = new Set(rappels.map((r) => r.tache.id)).size;
    const enRetard = new Set(rappels.filter((r) => r.retard > 0).map((r) => r.tache.id)).size;
    if (nombre === 1) {
        const seule = rappels[0];
        if (seule.retard > 0) {
            return seule.retard === 1
                ? `${seule.tache.titre} — en retard depuis hier`
                : `${seule.tache.titre} — en retard de ${seule.retard} jours`;
        }
        if (seule.offsetJours === 0)
            return `${seule.tache.titre} — aujourd'hui`;
        return `${seule.tache.titre} — dans ${seule.offsetJours} jour${seule.offsetJours > 1 ? 's' : ''}`;
    }
    if (enRetard === 0)
        return `${nombre} choses à faire`;
    if (enRetard === nombre)
        return `${nombre} choses en retard`;
    return `${nombre} choses à faire, dont ${enRetard} en retard`;
}
/**
 * La tâche me concerne-t-elle ?
 *
 * Une seule règle, partagée par l'écran d'accueil et par les annonces, pour
 * qu'ils ne puissent pas diverger : ce qui n'est assigné à personne concerne
 * tout le monde, ce qui est assigné concerne son destinataire.
 *
 * L'exception est l'escalade, et elle est délibérée. Le seuil de retard existe
 * précisément pour que l'autre parent soit prévenu de ce qui traîne : masquer
 * ces tâches-là viderait de son sens le seul mécanisme qui redistribue la
 * charge au lieu de la déplacer. Une tâche escaladée vers moi reste visible
 * même quand je ne veux voir que les miennes — sinon la notification
 * d'escalade mènerait à un écran où la tâche n'apparaît pas.
 */
export function meConcerne(tache, moiId) {
    if (!tache.assigneA)
        return true;
    if (tache.assigneA === moiId)
        return true;
    return Boolean(tache.escaladeVers) && tache.escaladeVers === moiId;
}
//# sourceMappingURL=rappels.js.map